import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class ManagerReport extends BaseObj {
    public ID: number;
    public __id: number;

    public Alternative: number | null;
    public __alternative: number | null;

    public DisciplinID: number | null;
    public __disciplinid: number | null;

    public ReportID: number;
    public __reportid: number;

    public Reporttype: string | null;
    public __reporttype: string | null;

    public SportID: number | null;
    public __sportid: number | null;

    public SportsLevelID: number | null;
    public __sportslevelid: number | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Alternative?: number | null;
        DisciplinID?: number | null;
        EntityState?: EntityState;
        ReportID?: number;
        Reporttype?: string | null;
        SportID?: number | null;
        SportsLevelID?: number | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<ManagerReport>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('ManagerReport tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Alternative = values.Alternative ?? null;
        this.__alternative = values.Alternative ?? null;
        Object.defineProperty(this, 'Alternative', {
            get(): number | null {
                return this.__alternative;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Alternative', this.__alternative, value);
                this.__alternative = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DisciplinID = values.DisciplinID ?? null;
        this.__disciplinid = values.DisciplinID ?? null;
        Object.defineProperty(this, 'DisciplinID', {
            get(): number | null {
                return this.__disciplinid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('DisciplinID', this.__disciplinid, value);
                this.__disciplinid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ReportID = values.ReportID ?? 0;
        this.__reportid = values.ReportID ?? 0;
        Object.defineProperty(this, 'ReportID', {
            get(): number {
                return this.__reportid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ReportID', this.__reportid, value);
                this.__reportid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Reporttype = values.Reporttype ?? null;
        this.__reporttype = values.Reporttype ?? null;
        Object.defineProperty(this, 'Reporttype', {
            get(): string | null {
                return this.__reporttype;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Reporttype', this.__reporttype, value);
                this.__reporttype = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SportID = values.SportID ?? null;
        this.__sportid = values.SportID ?? null;
        Object.defineProperty(this, 'SportID', {
            get(): number | null {
                return this.__sportid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('SportID', this.__sportid, value);
                this.__sportid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SportsLevelID = values.SportsLevelID ?? null;
        this.__sportslevelid = values.SportsLevelID ?? null;
        Object.defineProperty(this, 'SportsLevelID', {
            get(): number | null {
                return this.__sportslevelid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('SportsLevelID', this.__sportslevelid, value);
                this.__sportslevelid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Alternative": ${JSON.stringify(this.Alternative)}` +
            ',' +
            `"DisciplinID": ${JSON.stringify(this.DisciplinID)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"ReportID": ${JSON.stringify(this.ReportID)}` +
            ',' +
            `"Reporttype": ${JSON.stringify(this.Reporttype)}` +
            ',' +
            `"SportID": ${JSON.stringify(this.SportID)}` +
            ',' +
            `"SportsLevelID": ${JSON.stringify(this.SportsLevelID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of ManagerReport from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Alternative?: number | null;
        DisciplinID?: number | null;
        ReportID?: number;
        Reporttype?: string | null;
        SportID?: number | null;
        SportsLevelID?: number | null;
        EntityState?: 'Added' | 'Unchanged';
    }): ManagerReport {
        return new ManagerReport(values);
    }

    public static _FromServerJson(json: any): ManagerReport {
        return new ManagerReport({
            ID: json.ID,
            Alternative: json.Alternative ?? null,
            DisciplinID: json.DisciplinID ?? null,
            ReportID: json.ReportID,
            Reporttype: json.Reporttype ?? '',
            SportID: json.SportID ?? null,
            SportsLevelID: json.SportsLevelID ?? null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of ManagerReport from another instance of ManagerReport.
     * @param other The ManagerReport you want to clone
     * @returns A cloned ManagerReport
     */
    public static FromOther(other: ManagerReport): ManagerReport {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return ManagerReport._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ManagerReport.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ID' | 'Alternative' | 'DisciplinID' | 'ReportID' | 'Reporttype' | 'SportID' | 'SportsLevelID')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'ManagerReport';
}
